import React, { useState, useEffect, useRef, useCallback } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import {
  PlusIcon, 
  TrashIcon, 
  PencilIcon,
  EyeIcon,
  EyeSlashIcon,
  Squares2X2Icon,
  PhotoIcon,
  DocumentTextIcon,
  RectangleStackIcon,
  LinkIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  CameraIcon,
  CheckCircleIcon,
  Cog6ToothIcon,
  ClockIcon,
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  CodeBracketIcon,
  ClipboardDocumentIcon,
} from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { updateWebPage, fetchWebpage, fetchPageVersions, restorePageVersion } from "../../../../api/webpage/pages";
import toast from "react-hot-toast";
import { DocumentManagerV6 } from "../../Media/DocumentManagerV6";
import { PMDialog } from "../../../utils/PMDialog";
import { fetchDocuments, createDocument } from "../../../../api/document";
import { UploaderV4 } from "../../Media/Uploaderv4";
import { PMField } from "../../../utils/PMField";
import {
  BLOCK_TYPES,
  DEFAULT_BLOCKS,
  newPageBuilderBlockId,
} from "./pageBuilderSchema";
import {
  parsePastedBlocksJson,
  SAMPLE_BLOCKS_JSON,
} from "./pageBuilderImportUtils";
import { PROMANG_PAGEBUILDER_AI_GUIDE } from "./pageBuilderAiGuide";
import { QuillEditor } from "../../../utils/quill-editor";
import { AIGenerator } from "../../../utils/AIGenerator";
import { aiGeneratorConfigs } from "../../../../config/aiGeneratorConfigs";

/** Tailwind-only rich text display (lists, links, headings) without @tailwindcss/typography */
const PAGEBUILDER_RICH_HTML_CLASS =
  "max-w-none [&_p]:mb-3 last:[&_p]:mb-0 [&_p:first-child]:mt-0 [&_ul]:my-3 [&_ol]:my-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:my-1 [&_li]:pl-0.5 [&_a]:text-blue-600 [&_a]:underline [&_a:hover]:text-blue-800 [&_strong]:font-semibold [&_em]:italic [&_blockquote]:border-l-4 [&_blockquote]:border-slate-300 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-slate-700 [&_h1]:text-3xl [&_h1]:font-bold [&_h1]:my-4 [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:my-3 [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:my-2";

export const PageBuilder = ({ pageId, slug, onSave }) => {
  const { t } = useTranslation();
  const [blocks, setBlocks] = useState([]);
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [isPreview, setIsPreview] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [imagePickerOpen, setImagePickerOpen] = useState(false);
  const [imagePickerBlockId, setImagePickerBlockId] = useState(null);
  const [imageSettingsOpen, setImageSettingsOpen] = useState(false);
  const [imageSettingsBlock, setImageSettingsBlock] = useState(null);
  const [textEditorOpen, setTextEditorOpen] = useState(false);
  const [textEditorBlock, setTextEditorBlock] = useState(null);
  const [sectionSettingsOpen, setSectionSettingsOpen] = useState(false);
  const [sectionSettingsBlock, setSectionSettingsBlock] = useState(null);
  const [contextMenuOpen, setContextMenuOpen] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });
  const [contextMenuTarget, setContextMenuTarget] = useState(null); // null, blockId, or column info
  const [pageSettingsOpen, setPageSettingsOpen] = useState(false);
  const [pageSettings, setPageSettings] = useState({
    meta_title: "",
    meta_description: "",
    meta_keywords: "",
    show_title: true,
    page_width: "full"
  });
  const [isPublished, setIsPublished] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [versions, setVersions] = useState([]);
  const [isLoadingVersions, setIsLoadingVersions] = useState(false);
  const [currentPageData, setCurrentPageData] = useState(null);
  const [livePageUrl, setLivePageUrl] = useState(null);
  const [importBlocksOpen, setImportBlocksOpen] = useState(false);
  const [importPasteText, setImportPasteText] = useState("");
  const [importTab, setImportTab] = useState("paste");
  const [importMode, setImportMode] = useState("replace");
  const editorRef = useRef(null);
  const contextMenuRef = useRef(null);

  useEffect(() => {
    loadPage();
  }, [slug]);

  useEffect(() => {
    // Ensure editor area is scrollable
    if (editorRef.current) {
      editorRef.current.scrollTop = 0;
    }
  }, [blocks]);

  const loadPage = async () => {
    try {
      setIsLoading(true);
      const response = await fetchWebpage(slug);
      if (response.status === 200 && response.data) {
        setLivePageUrl(
          typeof response.data.live_page_url === "string" && response.data.live_page_url
            ? response.data.live_page_url
            : null
        );
        setBlocks(response.data.content?.blocks || []);
        // Load page settings
        if (response.data.web_page) {
          setCurrentPageData(response.data.web_page);
          setPageSettings({
            meta_title: response.data.web_page.meta_title || "",
            meta_description: response.data.web_page.meta_description || "",
            meta_keywords: response.data.web_page.meta_keywords || "",
            show_title: response.data.web_page.show_title !== false,
            page_width: response.data.web_page.page_width || "full"
          });
          setIsPublished(response.data.web_page.published || false);
        }
      }
      setIsLoading(false);
    } catch (error) {
      console.error("Error loading page:", error);
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!pageId) {
      toast.error("Side ID mangler. Kan ikke lagre.");
      console.error("Page ID is undefined:", { pageId, slug });
      return;
    }

    if (isSaving) {
      return; // Prevent multiple saves
    }

    setIsSaving(true);
    try {
      // Log blocks before saving to verify textAlign is present
      console.log("Saving blocks:", JSON.stringify(blocks, null, 2));
      blocks.forEach((block, index) => {
        if (block.type === BLOCK_TYPES.IMAGE) {
          console.log(`Block ${index} (IMAGE) textAlign:`, block.styles?.textAlign);
        }
      });
      
      const content = {
        blocks: blocks,
        version: Date.now(),
      };
      
      // Create a new version each time we save
      const response = await updateWebPage({
        id: pageId,
        title: currentPageData?.title,
        slug: currentPageData?.slug ?? slug,
        content: content,
        meta_title: pageSettings.meta_title,
        meta_description: pageSettings.meta_description,
        meta_keywords: pageSettings.meta_keywords,
        show_title: pageSettings.show_title,
        page_width: pageSettings.page_width,
        create_new_version: true
      });
      
      // If a new version was created (status 201), update page data but don't reload blocks
      // This preserves any unsaved changes in the editor
      if (response?.status === 201 && response?.data) {
        // Response contains the new page directly (not wrapped in web_page)
        const newPage = response.data;
        setCurrentPageData(newPage);
        // Only update page settings, not blocks (to preserve unsaved changes)
        setPageSettings({
          meta_title: newPage.meta_title || "",
          meta_description: newPage.meta_description || "",
          meta_keywords: newPage.meta_keywords || "",
          show_title: newPage.show_title !== false,
          page_width: newPage.page_width || "full"
        });
        setIsPublished(newPage.published || false);
        toast.success(`Side lagret! Ny versjon ${newPage.version || "opprettet"}.`);
      } else {
        toast.success("Side lagret!");
      }
      if (onSave) onSave();
    } catch (error) {
      console.error("Error saving page:", error);
      toast.error("Kunne ikke lagre side");
    } finally {
      setIsSaving(false);
    }
  };

  const handlePageSettingsSave = async (newSettings) => {
    if (!pageId) {
      toast.error("Side ID mangler. Kan ikke lagre innstillinger.");
      throw new Error("Page ID missing");
    }

    setPageSettings(newSettings);
    
    // Save settings immediately to API
    try {
      await updateWebPage({
        id: pageId,
        meta_title: newSettings.meta_title,
        meta_description: newSettings.meta_description,
        meta_keywords: newSettings.meta_keywords,
        show_title: newSettings.show_title,
        page_width: newSettings.page_width
      });
      toast.success("Sideinnstillinger lagret!");
      if (onSave) onSave();
    } catch (error) {
      console.error("Error saving page settings:", error);
      toast.error("Kunne ikke lagre sideinnstillinger");
      throw error; // Re-throw to let dialog handle it
    }
  };

  const handleTogglePublish = async () => {
    if (!pageId) {
      toast.error("Side ID mangler. Kan ikke publisere.");
      return;
    }

    setIsPublishing(true);
    try {
      const newPublishedStatus = !isPublished;
      await updateWebPage({
        id: pageId,
        published: newPublishedStatus
      });
      
      setIsPublished(newPublishedStatus);
      toast.success(newPublishedStatus ? "Side publisert!" : "Side avpublisert!");
      if (onSave) onSave();
    } catch (error) {
      console.error("Error publishing page:", error);
      toast.error("Kunne ikke oppdatere publiseringsstatus");
    } finally {
      setIsPublishing(false);
    }
  };

  const loadVersions = async () => {
    if (!pageId) return;
    
    setIsLoadingVersions(true);
    try {
      const response = await fetchPageVersions(pageId);
      if (response?.data?.data) {
        setVersions(response.data.data);
      }
    } catch (error) {
      console.error("Error loading versions:", error);
      toast.error("Kunne ikke laste versjoner");
    } finally {
      setIsLoadingVersions(false);
    }
  };

  const handleOpenVersions = () => {
    setVersionsOpen(true);
    loadVersions();
  };

  const handleRestoreVersion = async (versionId) => {
    if (!pageId) {
      toast.error("Side ID mangler. Kan ikke gjenopprette versjon.");
      return;
    }

    if (!confirm("Er du sikker på at du vil gjenopprette denne versjonen? Dette vil opprette en ny versjon med innholdet fra den valgte versjonen.")) {
      return;
    }

    try {
      await restorePageVersion(pageId, versionId);
      toast.success("Versjon gjenopprettet!");
      setVersionsOpen(false);
      // Reload the page to show the restored version
      loadPage();
      if (onSave) onSave();
    } catch (error) {
      console.error("Error restoring version:", error);
      toast.error("Kunne ikke gjenopprette versjon");
    }
  };

  const handleApplyImportedBlocks = () => {
    try {
      const next = parsePastedBlocksJson(importPasteText);
      if (importMode === "replace") {
        setBlocks(next);
      } else {
        setBlocks((prev) => [...prev, ...next]);
      }
      toast.success(t("webpage.editor.import_success", { count: next.length }));
      setImportBlocksOpen(false);
    } catch (err) {
      const code = err?.code || "unknown";
      const key = `webpage.editor.import_error_${code}`;
      const msg = t(key);
      toast.error(msg !== key ? msg : t("webpage.editor.import_error_unknown"));
    }
  };

  const handleCopyAiGuide = async () => {
    try {
      await navigator.clipboard.writeText(PROMANG_PAGEBUILDER_AI_GUIDE);
      toast.success(t("webpage.editor.ai_guide_copied"));
    } catch {
      toast.error(t("webpage.editor.ai_guide_copy_failed"));
    }
  };

  const handleAIGeneratedBlocks = useCallback(
    (generatedFields, meta) => {
      const raw = generatedFields?.blocks_json;
      if (raw == null || typeof raw !== "string" || !String(raw).trim()) {
        toast.error(t("webpage.editor.ai_parse_error"));
        return;
      }
      let next;
      try {
        next = parsePastedBlocksJson(raw);
      } catch (err) {
        const code = err?.code || "unknown";
        const key = `webpage.editor.import_error_${code}`;
        const msg = t(key);
        toast.error(msg !== key ? msg : t("webpage.editor.ai_parse_error"));
        return;
      }
      const applyMode = meta?.generationOptions?.apply_mode || "replace";
      if (applyMode === "append") {
        setBlocks((prev) => [...prev, ...next]);
      } else {
        setBlocks(next);
      }
      toast.success(t("webpage.editor.ai_apply_success", { count: next.length }));
    },
    [t]
  );

  const onDragEnd = (result) => {
    if (!result.destination) return;

    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;
    const sourceDroppableId = result.source.droppableId;
    const destDroppableId = result.destination.droppableId;

    // Handle dragging within main blocks
    if (sourceDroppableId === "blocks" && destDroppableId === "blocks") {
      const newBlocks = Array.from(blocks);
      const [removed] = newBlocks.splice(sourceIndex, 1);
      newBlocks.splice(destIndex, 0, removed);
      setBlocks(newBlocks);
    }
    // Handle dragging to sections
    else if (destDroppableId.startsWith("section-")) {
      const sectionId = destDroppableId.replace("section-", "");
      
      if (sourceDroppableId === "blocks") {
        // Moving from main blocks to section
        const newBlocks = [...blocks];
        const [movedBlock] = newBlocks.splice(sourceIndex, 1);
        
        const updateRecursive = (blockList) => {
          return blockList.map((block) => {
            if (block.id === sectionId && block.type === BLOCK_TYPES.SECTION) {
              const newChildren = [...(block.children || [])];
              newChildren.splice(destIndex, 0, movedBlock);
              return { ...block, children: newChildren };
            }
            if (block.children) {
              if (Array.isArray(block.children[0])) {
                return {
                  ...block,
                  children: block.children.map((column) => updateRecursive(column)),
                };
              } else {
                return {
                  ...block,
                  children: updateRecursive(block.children),
                };
              }
            }
            return block;
          });
        };
        setBlocks(updateRecursive(newBlocks));
      } else if (sourceDroppableId.startsWith("section-")) {
        // Moving between sections or within same section
        const sourceSectionId = sourceDroppableId.replace("section-", "");
        
        const findAndRemoveFromSection = (blockList, targetId, index) => {
          for (const b of blockList) {
            if (b.id === targetId && b.type === BLOCK_TYPES.SECTION) {
              const children = [...(b.children || [])];
              const [movedBlock] = children.splice(index, 1);
              return { movedBlock, children };
            }
            if (b.children) {
              if (Array.isArray(b.children[0])) {
                for (const column of b.children) {
                  const result = findAndRemoveFromSection(column, targetId, index);
                  if (result) return result;
                }
              } else {
                const result = findAndRemoveFromSection(b.children, targetId, index);
                if (result) return result;
              }
            }
          }
          return null;
        };
        
        const sourceResult = findAndRemoveFromSection(blocks, sourceSectionId, sourceIndex);
        if (sourceResult) {
          const updateBothSections = (blockList) => {
            return blockList.map((b) => {
              if (b.id === sourceSectionId && b.type === BLOCK_TYPES.SECTION) {
                return { ...b, children: sourceResult.children };
              }
              if (b.id === sectionId && b.type === BLOCK_TYPES.SECTION) {
                const newChildren = [...(b.children || [])];
                if (sourceSectionId === sectionId) {
                  // Moving within same section
                  const [movedBlock] = newChildren.splice(sourceIndex, 1);
                  newChildren.splice(destIndex, 0, movedBlock);
                } else {
                  // Moving from another section
                  newChildren.splice(destIndex, 0, sourceResult.movedBlock);
                }
                return { ...b, children: newChildren };
              }
              if (b.children) {
                if (Array.isArray(b.children[0])) {
                  return {
                    ...b,
                    children: b.children.map((column) => updateBothSections(column)),
                  };
                } else {
                  return {
                    ...b,
                    children: updateBothSections(b.children),
                  };
                }
              }
              return b;
            });
          };
          setBlocks(updateBothSections(blocks));
        }
      }
    }
    // Handle dragging to columns
    else if (destDroppableId.startsWith("column-")) {
      const columnMatch = destDroppableId.match(/column-(\d+)-(.+)/);
      if (columnMatch) {
        const blockIndex = parseInt(columnMatch[1]);
        const columnIndex = parseInt(columnMatch[2]);
        const block = blocks[blockIndex];
        
        if (block && block.type === BLOCK_TYPES.COLUMNS) {
          const newBlocks = [...blocks];
          const columnBlock = { ...newBlocks[blockIndex] };
          
          if (sourceDroppableId.startsWith("column-")) {
            // Moving between columns
            const sourceMatch = sourceDroppableId.match(/column-(\d+)-(\d+)/);
            if (sourceMatch) {
              const sourceBlockIndex = parseInt(sourceMatch[1]);
              const sourceColumnIndex = parseInt(sourceMatch[2]);
              const sourceColumnBlock = blocks[sourceBlockIndex];
              
              if (sourceColumnBlock && sourceColumnBlock.type === BLOCK_TYPES.COLUMNS) {
                const sourceColumn = sourceColumnBlock.children[sourceColumnIndex] || [];
                const [movedBlock] = sourceColumn.splice(sourceIndex, 1);
                
                if (sourceBlockIndex === blockIndex) {
                  // Same columns block
                  columnBlock.children = columnBlock.children.map((col, idx) => {
                    if (idx === sourceColumnIndex) return sourceColumn;
                    if (idx === columnIndex) {
                      const newCol = [...col];
                      newCol.splice(destIndex, 0, movedBlock);
                      return newCol;
                    }
                    return col;
                  });
                } else {
                  // Different columns blocks
                  const sourceBlock = { ...newBlocks[sourceBlockIndex] };
                  sourceBlock.children = sourceBlock.children.map((col, idx) =>
                    idx === sourceColumnIndex ? sourceColumn : col
                  );
                  newBlocks[sourceBlockIndex] = sourceBlock;
                  
                  columnBlock.children = columnBlock.children.map((col, idx) => {
                    if (idx === columnIndex) {
                      const newCol = [...col];
                      newCol.splice(destIndex, 0, movedBlock);
                      return newCol;
                    }
                    return col;
                  });
                }
              }
            }
          } else {
            // Moving from main blocks to column
            const [movedBlock] = blocks.splice(sourceIndex, 1);
            columnBlock.children = columnBlock.children.map((col, idx) => {
              if (idx === columnIndex) {
                const newCol = [...col];
                newCol.splice(destIndex, 0, movedBlock);
                return newCol;
              }
              return col;
            });
            newBlocks.splice(sourceIndex, 1);
          }
          
          newBlocks[blockIndex] = columnBlock;
          setBlocks(newBlocks);
        }
      }
    }
  };

  const addBlock = (blockType, parentBlockId = null, columnIndex = null) => {
    const newBlock = {
      ...DEFAULT_BLOCKS[blockType],
      id: newPageBuilderBlockId(),
    };
    
    if (parentBlockId && columnIndex !== null) {
      // Add to column
      setBlocks(blocks.map(block => {
        if (block.id === parentBlockId && block.type === BLOCK_TYPES.COLUMNS) {
          const newChildren = [...block.children];
          newChildren[columnIndex] = [...newChildren[columnIndex], newBlock];
          return { ...block, children: newChildren };
        }
        return block;
      }));
    } else if (parentBlockId) {
      // Add to section
      const updateRecursive = (blockList) => {
        return blockList.map(block => {
          if (block.id === parentBlockId && block.type === BLOCK_TYPES.SECTION) {
            const currentChildren = block.children || [];
            return { ...block, children: [...currentChildren, newBlock] };
          }
          if (block.children) {
            if (Array.isArray(block.children[0])) {
              // Columns structure
              return {
                ...block,
                children: block.children.map((column) => updateRecursive(column)),
              };
            } else {
              // Section structure - recursively search for the section
              return {
                ...block,
                children: updateRecursive(block.children),
              };
            }
          }
          return block;
        });
      };
      const updatedBlocks = updateRecursive(blocks);
      setBlocks(updatedBlocks);
    } else {
      setBlocks([...blocks, newBlock]);
    }
  };

  const updateBlock = (blockId, updates) => {
    setBlocks((currentBlocks) => {
      const updateRecursive = (blockList) => {
        return blockList.map((block) => {
          if (block.id === blockId) {
            // Deep merge styles if both block and updates have styles
            if (block.styles && updates.styles) {
              const mergedStyles = {
                ...block.styles,
                ...updates.styles
              };
              console.log("Merging styles:", {
                existing: block.styles,
                updates: updates.styles,
                merged: mergedStyles
              });
              return { 
                ...block, 
                ...updates,
                styles: mergedStyles
              };
            }
            // If updates has styles but block doesn't, create styles object
            if (updates.styles && !block.styles) {
              return {
                ...block,
                ...updates,
                styles: updates.styles
              };
            }
            return { ...block, ...updates };
          }
          if (block.children) {
            if (Array.isArray(block.children[0])) {
              // Columns structure
              return {
                ...block,
                children: block.children.map((column) => updateRecursive(column)),
              };
            } else {
              // Section structure
              return {
                ...block,
                children: updateRecursive(block.children),
              };
            }
          }
          return block;
        });
      };
      
      return updateRecursive(currentBlocks);
    });
  };

  const deleteBlock = (blockId) => {
    const deleteRecursive = (blockList) => {
      return blockList
        .filter((block) => block.id !== blockId)
        .map((block) => {
          if (block.children) {
            if (Array.isArray(block.children[0])) {
              return {
                ...block,
                children: block.children.map((column) => deleteRecursive(column)),
              };
            } else {
              return {
                ...block,
                children: deleteRecursive(block.children),
              };
            }
          }
          return block;
        });
    };
    
    setBlocks(deleteRecursive(blocks));
    setSelectedBlock(null);
  };

  const openImagePicker = (blockId) => {
    setImagePickerBlockId(blockId);
    setImagePickerOpen(true);
  };

  const openImageSettings = (block) => {
    setImageSettingsBlock(block);
    setImageSettingsOpen(true);
  };

  const openSectionSettings = (block) => {
    setSectionSettingsBlock(block);
    setSectionSettingsOpen(true);
  };

  const handleImageSelected = (document) => {
    if (imagePickerBlockId && document) {
      const imageUrl = document.file?.url || document.url || document.file_url;
      updateBlock(imagePickerBlockId, {
        src: imageUrl,
        alt: document.title || "",
        documentId: document.id,
        title: document.title || "",
      });
      setImagePickerOpen(false);
      setImagePickerBlockId(null);
      toast.success("Bilde lagt til");
    }
  };

  const handleImageSettingsSave = (updatedData) => {
    if (imageSettingsBlock) {
      console.log("Saving image settings:", updatedData);
      updateBlock(imageSettingsBlock.id, updatedData);
      // Verify the update was applied
      setTimeout(() => {
        const updatedBlock = findBlockById(blocks, imageSettingsBlock.id);
        console.log("Block after update:", updatedBlock);
        console.log("textAlign in styles:", updatedBlock?.styles?.textAlign);
      }, 100);
      setImageSettingsOpen(false);
      setImageSettingsBlock(null);
      toast.success("Bildeinnstillinger lagret");
    }
  };

  // Helper function to find a block by ID
  const findBlockById = (blockList, blockId) => {
    for (const block of blockList) {
      if (block.id === blockId) return block;
      if (block.children) {
        if (Array.isArray(block.children[0])) {
          // Columns structure
          for (const column of block.children) {
            const found = findBlockById(column, blockId);
            if (found) return found;
          }
        } else {
          // Section structure
          const found = findBlockById(block.children, blockId);
          if (found) return found;
        }
      }
    }
    return null;
  };

  const handleReplaceImage = () => {
    if (imageSettingsBlock) {
      setImagePickerBlockId(imageSettingsBlock.id);
      setImageSettingsOpen(false);
      setImagePickerOpen(true);
    }
  };

  const openTextEditor = (block) => {
    setTextEditorBlock(block);
    setTextEditorOpen(true);
  };

  const handleTextEditorSave = (updatedStyles, updatedContent) => {
    if (textEditorBlock) {
      updateBlock(textEditorBlock.id, {
        styles: { ...textEditorBlock.styles, ...updatedStyles },
        content: updatedContent !== undefined ? updatedContent : textEditorBlock.content,
      });
      setTextEditorOpen(false);
      setTextEditorBlock(null);
    }
  };

  const handleContextMenu = (e, blockId = null, parentBlockId = null, columnIndex = null) => {
    e.preventDefault();
    e.stopPropagation();
    
    setContextMenuTarget({ blockId, parentBlockId, columnIndex });
    setContextMenuPosition({ x: e.clientX, y: e.clientY });
    setContextMenuOpen(true);
  };

  // Adjust menu position after it's rendered to ensure it stays within viewport
  useEffect(() => {
    if (contextMenuOpen && contextMenuRef.current) {
      const menu = contextMenuRef.current;
      const menuRect = menu.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const padding = 10;
      
      let x = contextMenuPosition.x;
      let y = contextMenuPosition.y;
      
      // Adjust horizontal position
      if (x + menuRect.width > viewportWidth - padding) {
        x = viewportWidth - menuRect.width - padding;
      }
      if (x < padding) {
        x = padding;
      }
      
      // Adjust vertical position
      if (y + menuRect.height > viewportHeight - padding) {
        y = viewportHeight - menuRect.height - padding;
      }
      if (y < padding) {
        y = padding;
      }
      
      // Only update if position changed
      if (x !== contextMenuPosition.x || y !== contextMenuPosition.y) {
        setContextMenuPosition({ x, y });
      }
    }
  }, [contextMenuOpen, contextMenuPosition.x, contextMenuPosition.y]);

  const handleAddBlockFromMenu = (blockType) => {
    if (contextMenuTarget) {
      const { blockId, parentBlockId, columnIndex } = contextMenuTarget;
      
      // Priority 1: Add to column (has both parentBlockId and columnIndex)
      if (parentBlockId && columnIndex !== null) {
        addBlock(blockType, parentBlockId, columnIndex);
      } 
      // Priority 2: Add to section (has parentBlockId but no columnIndex)
      else if (parentBlockId) {
        addBlock(blockType, parentBlockId);
      } 
      // Priority 3: Add after a specific block
      else if (blockId) {
        // Check if blockId is a section
        const findBlockRecursive = (blockList, targetId) => {
          for (const block of blockList) {
            if (block.id === targetId) {
              return block;
            }
            if (block.children) {
              if (Array.isArray(block.children[0])) {
                // Columns structure
                for (const column of block.children) {
                  const found = findBlockRecursive(column, targetId);
                  if (found) return found;
                }
              } else {
                // Section structure
                const found = findBlockRecursive(block.children, targetId);
                if (found) return found;
              }
            }
          }
          return null;
        };
        
        const targetBlock = findBlockRecursive(blocks, blockId);
        if (targetBlock && targetBlock.type === BLOCK_TYPES.SECTION) {
          // Add to section's children
          addBlock(blockType, blockId);
        } else {
          // Add after selected block in main list
          const blockIndex = blocks.findIndex(b => b.id === blockId);
          if (blockIndex !== -1) {
            const newBlock = {
              ...DEFAULT_BLOCKS[blockType],
              id: newPageBuilderBlockId(),
            };
            const newBlocks = [...blocks];
            newBlocks.splice(blockIndex + 1, 0, newBlock);
            setBlocks(newBlocks);
          }
        }
      } 
      // Priority 4: Add to end of main blocks
      else {
        addBlock(blockType);
      }
    } else {
      // Add to end of main blocks if no specific target
      addBlock(blockType);
    }
    setContextMenuOpen(false);
    setContextMenuTarget(null);
  };

  const renderBlock = (block, index, parentBlockId = null, columnIndex = null) => {
    const isSelected = selectedBlock?.id === block.id;
    let droppableId = "blocks";
    if (parentBlockId && columnIndex !== null) {
      // Inside a column
      droppableId = `column-${blocks.findIndex(b => b.id === parentBlockId)}-${columnIndex}`;
    } else if (parentBlockId) {
      // Inside a section
      droppableId = `section-${parentBlockId}`;
    }

    return (
      <Draggable key={block.id} draggableId={block.id} index={index}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            className={`mb-4 relative rounded-md ${
              isSelected
                ? "ring-2 ring-blue-500 ring-offset-2 ring-offset-white pt-10"
                : ""
            } ${snapshot.isDragging ? "opacity-50" : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedBlock(block);
            }}
            onContextMenu={(e) => handleContextMenu(e, block.id, parentBlockId, columnIndex)}
          >
            {/* Kun én verktøylinje om gangen: vises når blokken er valgt (ikke hover — unngår rot i nestede seksjoner) */}
            {isSelected && (
              <div className="absolute right-1 top-1 z-30 flex items-center gap-0.5 rounded-lg border border-slate-200 bg-white/95 p-0.5 shadow-lg backdrop-blur-sm">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleContextMenu(e, block.id, parentBlockId, columnIndex);
                  }}
                  className="rounded-md p-1.5 text-blue-700 hover:bg-blue-50"
                  title="Legg til blokk"
                >
                  <PlusIcon className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteBlock(block.id);
                  }}
                  className="rounded-md p-1.5 text-red-600 hover:bg-red-50"
                  title="Slett blokk"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  {...provided.dragHandleProps}
                  className="rounded-md p-1.5 text-slate-600 hover:bg-slate-100 cursor-grab active:cursor-grabbing"
                  title="Dra for å flytte"
                >
                  <Squares2X2Icon className="h-4 w-4" />
                </button>
              </div>
            )}
            {renderBlockContent(block, parentBlockId, columnIndex)}
          </div>
        )}
      </Draggable>
    );
  };

  const renderBlockContent = (block, parentBlockId = null, columnIndex = null) => {
    switch (block.type) {
      case BLOCK_TYPES.TEXT:
        return (
          <div
            style={block.styles}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedBlock(block);
              openTextEditor(block);
            }}
            className="min-h-[50px] rounded-lg border border-transparent transition-colors hover:border-slate-200 hover:bg-slate-50/50 focus:outline-none focus:ring-2 focus:ring-blue-400/80 cursor-pointer"
          >
            <div
              className={PAGEBUILDER_RICH_HTML_CLASS}
              dangerouslySetInnerHTML={{ __html: block.content || "" }}
            />
          </div>
        );

      case BLOCK_TYPES.HEADER: {
        const HeaderTag = `h${block.level || 1}`;
        return (
          <HeaderTag
            style={block.styles}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedBlock(block);
              openTextEditor(block);
            }}
            className="focus:outline-none focus:ring-2 focus:ring-blue-300 rounded cursor-pointer"
          >
            {block.content}
          </HeaderTag>
        );
      }

      case BLOCK_TYPES.IMAGE: {
        // Get textAlign from styles, default to "left" if not set
        const textAlign = block.styles?.textAlign || "left";
        
        // Separate styles for the wrapper (textAlign) and image container
        const wrapperStyles = {
          textAlign: textAlign,
        };
        
        const imageContainerStyles = {
          display: "inline-block",
          width: block.styles?.width || "100%",
          maxWidth: block.styles?.maxWidth || "100%",
        };
        
        const imageStyles = {
          borderRadius: block.styles?.borderRadius || "0px",
          border: block.styles?.border || "none",
          boxShadow: block.styles?.boxShadow || "none",
          width: "100%",
          height: "auto",
        };
        
        return (
          <div style={wrapperStyles} className="relative">
            {block.src ? (
              <div 
                className="relative group/pb-img cursor-pointer"
                style={imageContainerStyles}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedBlock(block);
                  openImageSettings(block);
                }}
              >
                <img
                  src={block.src}
                  alt={block.alt || ""}
                  title={block.title || ""}
                  style={imageStyles}
                />
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover/pb-img:bg-opacity-20 transition-opacity rounded flex items-center justify-center" style={{ borderRadius: block.styles?.borderRadius || "0px" }}>
                  <div className="opacity-0 group-hover/pb-img:opacity-100 text-white text-sm font-medium bg-black bg-opacity-50 px-3 py-1 rounded">
                    Klikk for å redigere
                  </div>
                </div>
              </div>
            ) : (
              <div 
                className="border-2 border-dashed border-gray-300 rounded p-8 text-center cursor-pointer hover:border-blue-400"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedBlock(block);
                  openImagePicker(block.id);
                }}
              >
                <PhotoIcon className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                <p className="text-gray-500">Klikk for å legge til bilde</p>
              </div>
            )}
          </div>
        );
      }

      case BLOCK_TYPES.BUTTON:
        return (
          <div style={block.styles}>
            {selectedBlock?.id === block.id ? (
              <div className="space-y-2">
                <input
                  type="text"
                  placeholder="Knapp tekst"
                  value={block.text || ""}
                  onChange={(e) => updateBlock(block.id, { text: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                  onClick={(e) => e.stopPropagation()}
                />
                <input
                  type="text"
                  placeholder="Link URL"
                  value={block.link || ""}
                  onChange={(e) => updateBlock(block.id, { link: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                  onClick={(e) => e.stopPropagation()}
                />
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={block.styles?.backgroundColor || "#3B82F6"}
                    onChange={(e) =>
                      updateBlock(block.id, {
                        styles: { ...block.styles, backgroundColor: e.target.value },
                      })
                    }
                    className="w-12 h-10 border rounded"
                    onClick={(e) => e.stopPropagation()}
                  />
                  <input
                    type="color"
                    value={block.styles?.color || "#ffffff"}
                    onChange={(e) =>
                      updateBlock(block.id, {
                        styles: { ...block.styles, color: e.target.value },
                      })
                    }
                    className="w-12 h-10 border rounded"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              </div>
            ) : (
              <a
                href={block.link || "#"}
                style={block.styles}
                className="inline-block"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setSelectedBlock(block);
                }}
              >
                {block.text || "Klikk her"}
              </a>
            )}
          </div>
        );

      case BLOCK_TYPES.SECTION:
        return (
          <div
            style={{
              backgroundColor: block.backgroundColor || "#ffffff",
              padding: block.padding || "32px",
              borderRadius: block.borderRadius || "0px",
              margin: block.margin || "0px",
            }}
            className="rounded border-2 border-dashed border-gray-300 relative"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedBlock(block);
            }}
          >
            {selectedBlock?.id === block.id && (
              <div className="absolute -top-11 left-0 z-20">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    openSectionSettings(block);
                  }}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-800 shadow-md hover:bg-slate-50"
                  title="Rediger seksjon"
                >
                  <PencilIcon className="w-4 h-4 text-blue-600" />
                  Rediger seksjon
                </button>
              </div>
            )}
            <Droppable droppableId={`section-${block.id}`}>
              {(provided) => (
                <div 
                  {...provided.droppableProps} 
                  ref={provided.innerRef}
                  onContextMenu={(e) => handleContextMenu(e, null, block.id, null)}
                >
                  {block.children?.map((child, idx) => renderBlock(child, idx, block.id))}
                  {provided.placeholder}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleContextMenu(e, null, block.id, null);
                    }}
                    className="mt-2 w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded hover:border-blue-400 text-gray-500 hover:text-blue-500"
                  >
                    <PlusIcon className="w-5 h-5 mx-auto" />
                  </button>
                </div>
              )}
            </Droppable>
          </div>
        );

      case BLOCK_TYPES.COLUMNS: {
        const blockIndex = blocks.findIndex(b => b.id === block.id);
        return (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: `repeat(${block.columns}, 1fr)`,
              gap: block.gap,
            }}
            className="rounded"
          >
            {selectedBlock?.id === block.id && (
              <div className="col-span-full mb-2 p-2 bg-gray-100 rounded">
                <label className="block text-sm font-medium mb-1">
                  Antall kolonner:
                </label>
                <input
                  type="number"
                  min="1"
                  max="4"
                  value={block.columns || 2}
                  onChange={(e) => {
                    const newColumns = parseInt(e.target.value);
                    const currentColumns = block.columns || 2;
                    let newChildren = [...block.children];
                    
                    if (newColumns > currentColumns) {
                      // Add empty columns
                      for (let i = currentColumns; i < newColumns; i++) {
                        newChildren.push([]);
                      }
                    } else if (newColumns < currentColumns) {
                      // Remove columns
                      newChildren = newChildren.slice(0, newColumns);
                    }
                    
                    updateBlock(block.id, {
                      columns: newColumns,
                      children: newChildren,
                    });
                  }}
                  className="w-full px-3 py-2 border rounded"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            )}
            {block.children?.map((column, colIdx) => (
              <Droppable
                key={colIdx}
                droppableId={`column-${blockIndex}-${colIdx}`}
              >
                {(provided, snapshot) => (
                  <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    className={`border-2 border-dashed rounded p-4 min-h-[100px] ${
                      snapshot.isDraggingOver ? "border-blue-400 bg-blue-50" : "border-gray-300"
                    }`}
                    onContextMenu={(e) => handleContextMenu(e, null, block.id, colIdx)}
                  >
                    {column.map((child, idx) => renderBlock(child, idx, block.id, colIdx))}
                    {provided.placeholder}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleContextMenu(e, null, block.id, colIdx);
                      }}
                      className="mt-2 w-full px-2 py-1 border border-dashed border-gray-300 rounded hover:border-blue-400 text-gray-400 hover:text-blue-500 text-xs"
                    >
                      <PlusIcon className="w-4 h-4 mx-auto" />
                    </button>
                  </div>
                )}
              </Droppable>
            ))}
          </div>
        );
      }

      default:
        return <div>Ukjent blokk type</div>;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Laster...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto flex h-full bg-gray-50 relative">
      {/* Main Editor */}
      <div className="flex-1 flex flex-col relative w-full">
        {/* Toolbar */}
        <div className="bg-white border-b border-gray-200 p-4 flex items-center justify-between relative">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsPreview(!isPreview)}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded flex items-center gap-2"
            >
              {isPreview ? (
                <>
                  <EyeSlashIcon className="w-5 h-5" />
                  Rediger
                </>
              ) : (
                <>
                  <EyeIcon className="w-5 h-5" />
                  Forhåndsvisning
                </>
              )}
            </button>
            <button
              onClick={() => setPageSettingsOpen(true)}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded flex items-center gap-2"
            >
              <Cog6ToothIcon className="w-5 h-5" />
              Innstillinger
            </button>
            <button
              onClick={handleOpenVersions}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded flex items-center gap-2"
              title="Vis versjonshistorikk"
            >
              <ClockIcon className="w-5 h-5" />
              Versjoner
            </button>
            <button
              type="button"
              onClick={() => {
                setImportBlocksOpen(true);
                setImportTab("paste");
              }}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded flex items-center gap-2"
              title={t("webpage.editor.import_tooltip")}
            >
              <CodeBracketIcon className="w-5 h-5 shrink-0" />
              {t("webpage.editor.import_blocks")}
            </button>
            <AIGenerator
              module="webpage_page_builder"
              context={{
                page_title: currentPageData?.title || "",
                page_slug: currentPageData?.slug || slug || "",
                meta_description: pageSettings.meta_description || ""
              }}
              fieldsToGenerate={["blocks_json"]}
              onGenerate={handleAIGeneratedBlocks}
              config={aiGeneratorConfigs.webpage_page_builder}
              buttonLabel={t("webpage.editor.generate_with_ai")}
              className="!px-3 !py-2 text-sm"
            />
            {livePageUrl && (
              <a
                href={livePageUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-slate-900 text-white hover:bg-slate-800 rounded flex items-center gap-2 transition-colors"
                title={
                  isPublished
                    ? t("webpage.editor.open_live_title")
                    : t("webpage.editor.open_live_title_unpublished")
                }
              >
                <ArrowTopRightOnSquareIcon className="w-5 h-5 shrink-0" />
                {t("webpage.editor.open_live")}
              </a>
            )}
            <button
              onClick={handleTogglePublish}
              disabled={isPublishing}
              className={`px-4 py-2 rounded flex items-center gap-2 ${
                isPublished
                  ? "bg-green-100 text-green-800 hover:bg-green-200"
                  : "bg-yellow-100 text-yellow-800 hover:bg-yellow-200"
              } disabled:opacity-50 disabled:cursor-not-allowed`}
              title={isPublished ? "Avpubliser side" : "Publiser side"}
            >
              {isPublishing ? (
                <>
                  <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  {isPublished ? "Avpubliserer..." : "Publiserer..."}
                </>
              ) : (
                <>
                  {isPublished ? (
                    <>
                      <EyeSlashIcon className="w-5 h-5" />
                      Avpubliser
                    </>
                  ) : (
                    <>
                      <EyeIcon className="w-5 h-5" />
                      Publiser
                    </>
                  )}
                </>
              )}
            </button>
            {isPublished && (
              <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded">
                Publisert
              </span>
            )}
          </div>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isSaving ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Laster...
              </>
            ) : (
              "Lagre"
            )}
          </button>
        </div>

        {/* Editor/Preview Area */}
        <div 
          ref={editorRef}
          className="flex-1 overflow-x-auto p-8 pt-10 relative"
          onContextMenu={(e) => {
            if (e.target === editorRef.current || e.target.closest('.editor-content')) {
              handleContextMenu(e);
            }
          }}
        >
          <div className="mx-auto bg-white shadow-lg rounded-lg p-8 pb-10 h-full min-h-[min(100%,480px)] editor-content overflow-visible">
            {isPreview ? (
              <PageRenderer blocks={blocks} />
            ) : (
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="blocks">
                  {(provided) => (
                    <div {...provided.droppableProps} ref={provided.innerRef}>
                      {blocks.length === 0 ? (
                        <div className="text-center py-16 text-gray-400">
                          <p className="text-lg mb-2">Ingen blokker ennå</p>
                          <p className="text-sm mb-4">
                            Høyreklikk eller klikk på plustegn for å legge til blokker
                          </p>
                          <button
                            onClick={(e) => handleContextMenu(e)}
                            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                          >
                            <PlusIcon className="w-5 h-5 inline mr-2" />
                            Legg til blokk
                          </button>
                        </div>
                      ) : (
                        <>
                          {blocks.map((block, index) => renderBlock(block, index))}
                          <button
                            onClick={(e) => handleContextMenu(e)}
                            className="mt-4 w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded hover:border-blue-400 text-gray-500 hover:text-blue-500 flex items-center justify-center gap-2"
                          >
                            <PlusIcon className="w-5 h-5" />
                            <span>Legg til blokk</span>
                          </button>
                        </>
                      )}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            )}
          </div>
        </div>
      </div>

      {/* Image Picker Dialog */}
      {imagePickerOpen && (
        <ImagePickerDialog
          open={imagePickerOpen}
          onClose={() => {
            setImagePickerOpen(false);
            setImagePickerBlockId(null);
          }}
          onSelect={handleImageSelected}
          pageId={pageId}
        />
      )}

      {/* Image Settings Dialog */}
      {imageSettingsOpen && imageSettingsBlock && (
        <ImageSettingsDialog
          open={imageSettingsOpen}
          onClose={() => {
            setImageSettingsOpen(false);
            setImageSettingsBlock(null);
          }}
          block={imageSettingsBlock}
          onSave={handleImageSettingsSave}
          onReplaceImage={handleReplaceImage}
        />
      )}

      {/* Text Editor Dialog */}
      {textEditorOpen && textEditorBlock && (
        <TextEditorDialog
          open={textEditorOpen}
          onClose={() => {
            setTextEditorOpen(false);
            setTextEditorBlock(null);
          }}
          block={textEditorBlock}
          onSave={handleTextEditorSave}
        />
      )}

      {/* Section Settings Dialog */}
      {sectionSettingsOpen && sectionSettingsBlock && (
        <SectionSettingsDialog
          open={sectionSettingsOpen}
          onClose={() => {
            setSectionSettingsOpen(false);
            setSectionSettingsBlock(null);
          }}
          block={sectionSettingsBlock}
          onSave={(updates) => {
            updateBlock(sectionSettingsBlock.id, updates);
            setSectionSettingsOpen(false);
            setSectionSettingsBlock(null);
          }}
        />
      )}

      {/* Page Settings Dialog */}
      {pageSettingsOpen && (
        <PageSettingsDialog
          open={pageSettingsOpen}
          onClose={() => setPageSettingsOpen(false)}
          settings={pageSettings}
          onSave={handlePageSettingsSave}
        />
      )}

      {/* Versions History Dialog */}
      {versionsOpen && (
        <VersionsHistoryDialog
          open={versionsOpen}
          onClose={() => setVersionsOpen(false)}
          versions={versions}
          isLoading={isLoadingVersions}
          currentPageId={currentPageData?.id}
          onRestore={handleRestoreVersion}
        />
      )}

      <PMDialog
        open={importBlocksOpen}
        onClose={() => setImportBlocksOpen(false)}
        title="webpage.editor.import_dialog_title"
        size="6xl"
        panelClassName="!max-w-6xl w-full"
      >
        <div className="flex min-h-0 flex-col gap-4">
          <div className="flex flex-wrap gap-1 border-b border-gray-200 pb-2">
            <button
              type="button"
              onClick={() => setImportTab("paste")}
              className={`rounded-lg px-3 py-2 text-sm font-medium ${
                importTab === "paste"
                  ? "bg-blue-100 text-blue-900"
                  : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              {t("webpage.editor.import_tab_paste")}
            </button>
            <button
              type="button"
              onClick={() => setImportTab("guide")}
              className={`rounded-lg px-3 py-2 text-sm font-medium ${
                importTab === "guide"
                  ? "bg-blue-100 text-blue-900"
                  : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              {t("webpage.editor.import_tab_guide")}
            </button>
          </div>

          {importTab === "paste" ? (
            <>
              <p className="text-sm text-gray-600">{t("webpage.editor.import_hint")}</p>
              <textarea
                value={importPasteText}
                onChange={(e) => setImportPasteText(e.target.value)}
                className="min-h-[220px] w-full flex-1 rounded-lg border border-gray-300 font-mono text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                spellCheck={false}
                placeholder='{ "blocks": [ { "type": "header", "level": 1, "content": "Tittel" } ] }'
              />
              <div className="flex flex-wrap gap-4 text-sm">
                <label className="inline-flex cursor-pointer items-center gap-2">
                  <input
                    type="radio"
                    name="importMode"
                    checked={importMode === "replace"}
                    onChange={() => setImportMode("replace")}
                  />
                  {t("webpage.editor.import_replace")}
                </label>
                <label className="inline-flex cursor-pointer items-center gap-2">
                  <input
                    type="radio"
                    name="importMode"
                    checked={importMode === "append"}
                    onChange={() => setImportMode("append")}
                  />
                  {t("webpage.editor.import_append")}
                </label>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setImportPasteText(SAMPLE_BLOCKS_JSON)}
                  className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  {t("webpage.editor.import_sample")}
                </button>
                <button
                  type="button"
                  onClick={handleApplyImportedBlocks}
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                >
                  {t("webpage.editor.import_apply")}
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-gray-600">{t("webpage.editor.ai_guide_intro")}</p>
              <pre className="max-h-[min(420px,50vh)] overflow-auto rounded-lg bg-slate-50 p-4 text-left text-xs leading-relaxed text-slate-800 whitespace-pre-wrap font-mono">
                {PROMANG_PAGEBUILDER_AI_GUIDE}
              </pre>
              <button
                type="button"
                onClick={handleCopyAiGuide}
                className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
              >
                <ClipboardDocumentIcon className="h-5 w-5" />
                {t("webpage.editor.copy_ai_guide")}
              </button>
            </>
          )}
        </div>
      </PMDialog>

      {/* Context Menu */}
      {contextMenuOpen && (
        <div
          ref={contextMenuRef}
          className="fixed bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-2 min-w-[200px] max-h-[80vh] overflow-y-auto"
          style={{
            left: `${contextMenuPosition.x}px`,
            top: `${contextMenuPosition.y}px`,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-2 py-1 text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">
            Legg til blokk
          </div>
          <button
            onClick={() => handleAddBlockFromMenu(BLOCK_TYPES.TEXT)}
            className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-2"
          >
            <DocumentTextIcon className="w-5 h-5 text-gray-600" />
            <span>Tekst</span>
          </button>
          <button
            onClick={() => handleAddBlockFromMenu(BLOCK_TYPES.IMAGE)}
            className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-2"
          >
            <PhotoIcon className="w-5 h-5 text-gray-600" />
            <span>Bilde</span>
          </button>
          <button
            onClick={() => handleAddBlockFromMenu(BLOCK_TYPES.HEADER)}
            className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-2"
          >
            <RectangleStackIcon className="w-5 h-5 text-gray-600" />
            <span>Overskrift</span>
          </button>
          <button
            onClick={() => handleAddBlockFromMenu(BLOCK_TYPES.SECTION)}
            className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-2"
          >
            <Squares2X2Icon className="w-5 h-5 text-gray-600" />
            <span>Seksjon</span>
          </button>
          <button
            onClick={() => handleAddBlockFromMenu(BLOCK_TYPES.BUTTON)}
            className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-2"
          >
            <LinkIcon className="w-5 h-5 text-gray-600" />
            <span>Knapp</span>
          </button>
          <button
            onClick={() => handleAddBlockFromMenu(BLOCK_TYPES.COLUMNS)}
            className="w-full px-4 py-2 text-left hover:bg-gray-100 flex items-center gap-2"
          >
            <Squares2X2Icon className="w-5 h-5 text-gray-600" />
            <span>Kolonner</span>
          </button>
        </div>
      )}
    </div>
  );
};


// Image Picker Dialog Component - Shows all company images with search and pagination
const ImagePickerDialog = ({ open, onClose, onSelect, pageId }) => {
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [perPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [filesToUpload, setFilesToUpload] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadedCount, setUploadedCount] = useState(0);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const uploadFunctionRef = useRef(null);
  const [resetUploader, setResetUploader] = useState(null);
  const dropZoneRef = useRef(null);

  useEffect(() => {
    if (open) {
      loadDocuments();
    } else {
      // Reset when dialog closes
      setSearchQuery("");
      setPage(1);
      setFilesToUpload([]);
    }
  }, [open, page, searchQuery]);

  const loadDocuments = async () => {
    setIsLoading(true);
    try {
      const response = await fetchDocuments({
        page: page,
        per_page: perPage,
        query: searchQuery,
        filters: {},
        sortBy: "created_at",
        sort: "DESC",
      });

      if (response && response.documents) {
        // Filter to only show images based on filename extension
        const images = response.documents.filter((doc) => {
          const filename = doc?.file?.filename?.toLowerCase() || "";
          const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp'];
          return imageExtensions.some(ext => filename.endsWith(ext));
        });
        setDocuments(images);
        // Use totalCount from API, but adjust if we filtered out some documents
        // Since we're filtering client-side, we need to estimate the total
        // For now, use the filtered count as total (pagination will be approximate)
        setTotalCount(images.length);
      }
    } catch (error) {
      console.error("Error loading documents:", error);
      toast.error("Kunne ikke laste bilder");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelect = (document) => {
    onSelect(document);
  };

  const handleFileSelection = useCallback((event) => {
    const files = event.target?.files || (event.files ? Array.from(event.files) : []);
    const selectedFiles = Array.from(files);
    
    if (!selectedFiles.length) {
      return;
    }

    if (!pageId) {
      toast.error("Mangler side ID for opplasting");
      return;
    }

    const newFiles = selectedFiles.map((file) => ({
      file,
      previewUrl: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
      title: file.name.replace(/\.[^/.]+$/, ""),
      module: "web_page",
      moduleParam: pageId,
    }));

    setFilesToUpload((prev) => [...prev, ...newFiles]);
    setUploadDialogOpen(true);
    
    if (event.target && event.target.value) {
      event.target.value = '';
    }
  }, [pageId]);

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dropZoneRef.current?.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      const files = e.dataTransfer.files;
      if (files.length > 0) {
        handleFileSelection({ target: { files } });
      }
    },
    [handleFileSelection]
  );

  const handleUploadComplete = (uploadedFiles) => {
    // Reload documents after upload
    loadDocuments();
    setFilesToUpload([]);
    setIsUploading(false);
    setUploadProgress({});
    setUploadedCount(0);
    setUploadDialogOpen(false);
    toast.success(`${uploadedFiles.length} bilde(r) lastet opp`);
    if (resetUploader) {
      resetUploader();
    }
  };

  const handleProgressChange = (progress) => {
    setUploadProgress((prev) => ({ ...prev, ...progress }));
  };

  const handleFileUploadComplete = () => {
    setUploadedCount((prev) => prev + 1);
  };

  const totalPages = Math.ceil(totalCount / perPage);

  return (
    <PMDialog open={open} onClose={onClose} title="Velg bilde" size="4xl">
      <div className="space-y-4">
        {/* Search */}
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Søk etter bilder..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(1);
            }}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Upload Button */}
        <div
          ref={dropZoneRef}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-lg p-4 text-center transition-all cursor-pointer ${
            isDragging
              ? "border-blue-500 bg-blue-50"
              : "border-gray-300 bg-gray-50 hover:border-blue-400 hover:bg-blue-50"
          }`}
          onClick={() => fileInputRef.current?.click()}
        >
          <CameraIcon className="h-8 w-8 mx-auto mb-2 text-gray-400" />
          <p className="text-sm font-medium text-gray-700">
            {isDragging ? "Slipp bildet her" : "Klikk eller dra bilder hit for å laste opp"}
          </p>
          <p className="text-xs text-gray-500 mt-1">Eller velg fra eksisterende bilder nedenfor</p>
        </div>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="sr-only"
          onChange={handleFileSelection}
        />

        {/* Image Grid */}
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="text-gray-500">Laster bilder...</div>
          </div>
        ) : documents.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <PhotoIcon className="w-16 h-16 mb-4" />
            <p>Ingen bilder funnet</p>
            <p className="text-sm mt-2">Last opp bilder ved å bruke knappen over</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-4 gap-4 max-h-[500px] overflow-y-auto">
              {documents.map((doc) => {
                const imageUrl = doc.file?.url || doc.url || doc.file_url;
                return (
                  <div
                    key={doc.id}
                    onClick={() => handleSelect(doc)}
                    className="relative aspect-square border-2 border-gray-200 rounded-lg overflow-hidden cursor-pointer hover:border-blue-500 hover:shadow-lg transition-all group/pb-picker"
                  >
                    {imageUrl ? (
                      <>
                        <img
                          src={imageUrl}
                          alt={doc.title || ""}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Crect fill='%23ddd' width='100' height='100'/%3E%3Ctext fill='%23999' x='50%25' y='50%25' text-anchor='middle' dy='.3em'%3EBilde%3C/text%3E%3C/svg%3E";
                          }}
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover/pb-picker:bg-opacity-40 transition-opacity flex items-center justify-center">
                          <div className="opacity-0 group-hover/pb-picker:opacity-100 text-white text-xs font-medium text-center px-2">
                            {doc.title || "Uten tittel"}
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gray-100">
                        <PhotoIcon className="w-8 h-8 text-gray-400" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between pt-4 border-t">
                <div className="text-sm text-gray-600">
                  Viser {Math.min((page - 1) * perPage + 1, totalCount)} - {Math.min(page * perPage, totalCount)} av {totalCount} bilder
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Forrige
                  </button>
                  <div className="px-4 py-2 text-sm">
                    Side {page} av {totalPages}
                  </div>
                  <button
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page >= totalPages}
                    className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Neste
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Upload Dialog */}
      {uploadDialogOpen && (
        <ImageUploadDialog
          open={uploadDialogOpen}
          onClose={() => {
            setUploadDialogOpen(false);
            setFilesToUpload([]);
          }}
          filesToUpload={filesToUpload}
          module="web_page"
          moduleParam={pageId}
          uploadFunctionRef={uploadFunctionRef}
          onUploadComplete={handleUploadComplete}
          onProgressChange={handleProgressChange}
          onFileUploadComplete={handleFileUploadComplete}
          isUploading={isUploading}
          uploadProgress={uploadProgress}
          uploadedCount={uploadedCount}
          setIsUploading={setIsUploading}
          setUploadedCount={setUploadedCount}
          setUploadProgress={setUploadProgress}
          resetUploader={resetUploader}
          setResetUploader={setResetUploader}
          fileInputRef={fileInputRef}
          handleFileSelection={handleFileSelection}
          onTitleChange={(index, title) => {
            setFilesToUpload((prev) => {
              const updated = [...prev];
              updated[index].title = title;
              return updated;
            });
          }}
          onDeleteFile={(index) => {
            setFilesToUpload((prev) => {
              const updated = prev.filter((_, i) => i !== index);
              if (updated.length === 0) {
                setUploadDialogOpen(false);
              }
              return updated;
            });
          }}
        />
      )}

      {/* Uploader Component */}
      <UploaderV4
        filesToUpload={filesToUpload}
        onUploadStart={(uploadFn) => {
          if (uploadFn && typeof uploadFn === "function") {
            uploadFunctionRef.current = () => {
              setIsUploading(true);
              setUploadedCount(0);
              setUploadProgress({});
              return uploadFn().catch((error) => {
                setIsUploading(false);
                throw error;
              });
            };
          }
        }}
        onUploadComplete={handleUploadComplete}
        onProgressChange={handleProgressChange}
        onFileUploadComplete={handleFileUploadComplete}
        onReset={setResetUploader}
      />
    </PMDialog>
  );
};

// Versions History Dialog Component
const VersionsHistoryDialog = ({ open, onClose, versions, isLoading, currentPageId, onRestore }) => {
  const formatDate = (dateString) => {
    if (!dateString) return "Ukjent dato";
    const date = new Date(dateString);
    return date.toLocaleString("nb-NO", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <PMDialog open={open} onClose={onClose} title="Versjonshistorikk" size="3xl">
      <div className="space-y-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="text-gray-500">Laster versjoner...</div>
          </div>
        ) : versions.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <p>Ingen versjoner funnet</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-[600px] overflow-y-auto">
            {versions.map((version, index) => {
              const isCurrent = version.id === currentPageId;
              return (
                <div
                  key={version.id}
                  className={`border rounded-lg p-4 ${
                    isCurrent ? "border-blue-500 bg-blue-50" : "border-gray-200"
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-semibold text-lg">
                          Versjon {version.version}
                        </span>
                        {isCurrent && (
                          <span className="px-2 py-1 bg-blue-500 text-white text-xs font-medium rounded">
                            Nåværende
                          </span>
                        )}
                        {version.published && (
                          <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded">
                            Publisert
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-600 space-y-1">
                        <p>
                          <span className="font-medium">Opprettet:</span>{" "}
                          {formatDate(version.created_at)}
                        </p>
                        {version.created_by && (
                          <p>
                            <span className="font-medium">Av:</span>{" "}
                            {version.created_by.name || "Ukjent"}
                          </p>
                        )}
                        {version.updated_at && version.updated_at !== version.created_at && (
                          <p>
                            <span className="font-medium">Oppdatert:</span>{" "}
                            {formatDate(version.updated_at)}
                          </p>
                        )}
                        {version.title && (
                          <p>
                            <span className="font-medium">Tittel:</span> {version.title}
                          </p>
                        )}
                      </div>
                    </div>
                    {!isCurrent && (
                      <button
                        onClick={() => onRestore(version.id)}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-2"
                        title="Gjenopprett denne versjonen"
                      >
                        <ArrowPathIcon className="w-4 h-4" />
                        Gjenopprett
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </PMDialog>
  );
};

// Page Settings Dialog Component
const PageSettingsDialog = ({ open, onClose, settings, onSave }) => {
  const [localSettings, setLocalSettings] = useState(settings);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setLocalSettings(settings);
  }, [settings]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(localSettings);
      onClose();
    } catch (error) {
      // Error handling is done in parent component
    } finally {
      setIsSaving(false);
    }
  };

  const pageWidthOptions = [
    { value: "full", label: "Full bredde" },
    { value: "wide", label: "Bred (1400px)" },
    { value: "standard", label: "Standard (1200px)" },
    { value: "narrow", label: "Smal (800px)" },
  ];

  return (
    <PMDialog open={open} onClose={onClose} title="Sideinnstillinger" size="2xl">
      <div className="space-y-4">
        {/* SEO Settings */}
        <div className="border-t pt-4">
          <h3 className="text-lg font-semibold mb-4">SEO-innstillinger</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Meta-tittel</label>
              <input
                type="text"
                value={localSettings.meta_title || ""}
                onChange={(e) => setLocalSettings({ ...localSettings, meta_title: e.target.value })}
                placeholder="SEO-tittel (hvis tom, brukes sidetittel)"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Tittel som vises i søkeresultater og nettleser-fane
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Meta-beskrivelse</label>
              <textarea
                value={localSettings.meta_description || ""}
                onChange={(e) => setLocalSettings({ ...localSettings, meta_description: e.target.value })}
                placeholder="Beskrivelse av siden for søkemotorer"
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Kort beskrivelse som vises i søkeresultater (anbefalt: 150-160 tegn)
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Nøkkelord</label>
              <input
                type="text"
                value={localSettings.meta_keywords || ""}
                onChange={(e) => setLocalSettings({ ...localSettings, meta_keywords: e.target.value })}
                placeholder="nøkkelord1, nøkkelord2, nøkkelord3"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Kommaseparerte nøkkelord for SEO
              </p>
            </div>
          </div>
        </div>

        {/* Display Settings */}
        <div className="border-t pt-4">
          <h3 className="text-lg font-semibold mb-4">Visningsinnstillinger</h3>
          
          <div className="space-y-4">
            <div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={localSettings.show_title !== false}
                  onChange={(e) => setLocalSettings({ ...localSettings, show_title: e.target.checked })}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm font-medium">Vis sidetittel</span>
              </label>
              <p className="text-xs text-gray-500 mt-1 ml-6">
                Viser sidens tittel øverst på siden
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Sidebredde</label>
              <select
                value={localSettings.page_width || "full"}
                onChange={(e) => setLocalSettings({ ...localSettings, page_width: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {pageWidthOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Maksimal bredde på innholdet
              </p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          <button
            onClick={onClose}
            disabled={isSaving}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Avbryt
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isSaving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Lagrer...
              </>
            ) : (
              "Lagre"
            )}
          </button>
        </div>
      </div>
    </PMDialog>
  );
};

// Image Settings Dialog Component
const ImageSettingsDialog = ({ open, onClose, block, onSave, onReplaceImage }) => {
  const [alt, setAlt] = useState(block?.alt || "");
  const [title, setTitle] = useState(block?.title || "");
  const [width, setWidth] = useState(block?.styles?.width || "100%");
  const [maxWidth, setMaxWidth] = useState(block?.styles?.maxWidth || "100%");
  const [padding, setPadding] = useState(block?.styles?.padding || "16px");
  const [textAlign, setTextAlign] = useState(block?.styles?.textAlign || "left");
  const [borderRadius, setBorderRadius] = useState(block?.styles?.borderRadius || "0px");
  const [border, setBorder] = useState(() => {
    if (!block?.styles?.border) return "none";
    // Check if border is "none" or an actual border string
    return block.styles.border === "none" ? "none" : "solid";
  });
  const [borderWidth, setBorderWidth] = useState(() => {
    if (!block?.styles?.border || block.styles.border === "none") return "1px";
    // Extract border width from border string (e.g., "2px solid #000000")
    const match = block.styles.border.match(/^(\d+px)/);
    return match ? match[1] : "1px";
  });
  const [borderColor, setBorderColor] = useState(() => {
    if (!block?.styles?.border || block.styles.border === "none") return "#000000";
    // Extract border color from border string
    const match = block.styles.border.match(/#[0-9A-Fa-f]{6}|rgb\([^)]+\)|rgba\([^)]+\)/);
    return match ? match[0] : "#000000";
  });
  const [boxShadow, setBoxShadow] = useState(block?.styles?.boxShadow || "none");

  useEffect(() => {
    if (block) {
      setAlt(block.alt || "");
      setTitle(block.title || "");
      setWidth(block.styles?.width || "100%");
      setMaxWidth(block.styles?.maxWidth || "100%");
      setPadding(block.styles?.padding || "16px");
      setTextAlign(block.styles?.textAlign || "left");
      setBorderRadius(block.styles?.borderRadius || "0px");
      
      // Parse border
      if (block.styles?.border && block.styles.border !== "none") {
        setBorder("solid");
        const borderMatch = block.styles.border.match(/^(\d+px)\s+solid\s+(.+)$/);
        if (borderMatch) {
          setBorderWidth(borderMatch[1]);
          setBorderColor(borderMatch[2]);
        }
      } else {
        setBorder("none");
        setBorderWidth("1px");
        setBorderColor("#000000");
      }
      
      setBoxShadow(block.styles?.boxShadow || "none");
    }
  }, [block]);

  const handleSave = () => {
    const styles = {
      ...block.styles,
      width,
      maxWidth,
      padding,
      textAlign,
      borderRadius,
      border: border === "none" ? "none" : `${borderWidth} solid ${borderColor}`,
      borderWidth: border !== "none" ? borderWidth : undefined,
      borderColor: border !== "none" ? borderColor : undefined,
      boxShadow,
    };
    
    // Clean up undefined values
    Object.keys(styles).forEach(key => {
      if (styles[key] === undefined) {
        delete styles[key];
      }
    });
    
    onSave({
      alt,
      title,
      styles,
    });
  };

  return (
    <PMDialog open={open} onClose={onClose} title="Bildeinnstillinger" size="2xl">
      <div className="space-y-4">
        {/* Preview */}
        {block?.src && (
          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
            <img
              src={block.src}
              alt={alt}
              className="w-full h-auto rounded max-h-64 object-contain mx-auto"
            />
          </div>
        )}

        {/* Replace Image Button */}
        <button
          onClick={onReplaceImage}
          className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center justify-center gap-2"
        >
          <PhotoIcon className="w-5 h-5" />
          Erstatt bilde
        </button>

        {/* Settings */}
        <div className="space-y-4 border-t pt-4">
          <div>
            <label className="block text-sm font-medium mb-2">Tittel</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Bildetittel"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Alt-tekst</label>
            <input
              type="text"
              value={alt}
              onChange={(e) => setAlt(e.target.value)}
              placeholder="Beskrivelse av bildet"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Alt-tekst hjelper skjermlesere og vises hvis bildet ikke kan lastes
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Justering</label>
            <select
              value={textAlign}
              onChange={(e) => setTextAlign(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="left">Venstre</option>
              <option value="center">Senter</option>
              <option value="right">Høyre</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Hvordan bildet skal justeres horisontalt
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Bredde</label>
              <input
                type="text"
                value={width}
                onChange={(e) => setWidth(e.target.value)}
                placeholder="100%"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Maks bredde</label>
              <input
                type="text"
                value={maxWidth}
                onChange={(e) => setMaxWidth(e.target.value)}
                placeholder="100%"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Padding</label>
            <input
              type="text"
              value={padding}
              onChange={(e) => setPadding(e.target.value)}
              placeholder="16px"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Avrundede hjørner</label>
            <input
              type="text"
              value={borderRadius}
              onChange={(e) => setBorderRadius(e.target.value)}
              placeholder="0px eller 8px"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Eksempel: &quot;0px&quot;, &quot;8px&quot;, &quot;16px&quot; eller &quot;50%&quot; for sirkel
            </p>
          </div>

          <div className="border-t pt-4">
            <label className="flex items-center gap-2 mb-4">
              <input
                type="checkbox"
                checked={border !== "none"}
                onChange={(e) => setBorder(e.target.checked ? "solid" : "none")}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm font-medium">Vis ramme</span>
            </label>

            {border !== "none" && (
              <div className="grid grid-cols-2 gap-4 ml-6">
                <div>
                  <label className="block text-sm font-medium mb-2">Rammebredde</label>
                  <input
                    type="text"
                    value={borderWidth}
                    onChange={(e) => setBorderWidth(e.target.value)}
                    placeholder="1px"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Rammefarge</label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={borderColor}
                      onChange={(e) => setBorderColor(e.target.value)}
                      className="w-16 h-10 border rounded"
                    />
                    <input
                      type="text"
                      value={borderColor}
                      onChange={(e) => setBorderColor(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="#000000"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Skygge</label>
            <input
              type="text"
              value={boxShadow}
              onChange={(e) => setBoxShadow(e.target.value)}
              placeholder="none eller 0 4px 6px rgba(0,0,0,0.1)"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Eksempel: &quot;none&quot;, &quot;0 4px 6px rgba(0,0,0,0.1)&quot; eller &quot;0 10px 20px rgba(0,0,0,0.2)&quot;
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
          >
            Avbryt
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Lagre
          </button>
        </div>
      </div>
    </PMDialog>
  );
};

// Section Settings Dialog Component
const SectionSettingsDialog = ({ open, onClose, block, onSave }) => {
  const [backgroundColor, setBackgroundColor] = useState(block?.backgroundColor || "#ffffff");
  const [padding, setPadding] = useState(block?.padding || "32px");
  const [paddingTop, setPaddingTop] = useState("");
  const [paddingRight, setPaddingRight] = useState("");
  const [paddingBottom, setPaddingBottom] = useState("");
  const [paddingLeft, setPaddingLeft] = useState("");
  const [useIndividualPadding, setUseIndividualPadding] = useState(false);
  const [borderRadius, setBorderRadius] = useState(block?.borderRadius || "0px");
  const [margin, setMargin] = useState(block?.margin || "0px");

  useEffect(() => {
    if (block) {
      setBackgroundColor(block.backgroundColor || "#ffffff");
      setPadding(block.padding || "32px");
      setBorderRadius(block.borderRadius || "0px");
      setMargin(block.margin || "0px");
      
      // Check if padding is individual or combined
      if (block.paddingTop || block.paddingRight || block.paddingBottom || block.paddingLeft) {
        setUseIndividualPadding(true);
        setPaddingTop(block.paddingTop || "");
        setPaddingRight(block.paddingRight || "");
        setPaddingBottom(block.paddingBottom || "");
        setPaddingLeft(block.paddingLeft || "");
      } else {
        setUseIndividualPadding(false);
      }
    }
  }, [block]);

  const handleSave = () => {
    const updates = {
      backgroundColor,
      borderRadius,
      margin,
    };

    if (useIndividualPadding) {
      updates.paddingTop = paddingTop || "0px";
      updates.paddingRight = paddingRight || "0px";
      updates.paddingBottom = paddingBottom || "0px";
      updates.paddingLeft = paddingLeft || "0px";
      // Also set combined padding for backwards compatibility
      updates.padding = `${paddingTop || "0px"} ${paddingRight || "0px"} ${paddingBottom || "0px"} ${paddingLeft || "0px"}`;
    } else {
      updates.padding = padding;
      // Clear individual padding if switching back to combined
      updates.paddingTop = null;
      updates.paddingRight = null;
      updates.paddingBottom = null;
      updates.paddingLeft = null;
    }

    onSave(updates);
  };

  return (
    <PMDialog open={open} onClose={onClose} title="Seksjonsinnstillinger" size="2xl">
      <div className="space-y-4">
        {/* Preview */}
        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
          <div
            style={{
              backgroundColor,
              padding: useIndividualPadding 
                ? `${paddingTop || "0px"} ${paddingRight || "0px"} ${paddingBottom || "0px"} ${paddingLeft || "0px"}`
                : padding,
              borderRadius,
              margin,
              minHeight: "100px",
            }}
            className="border-2 border-dashed border-gray-300"
          >
            <p className="text-sm text-gray-500 text-center py-4">Forhåndsvisning av seksjon</p>
          </div>
        </div>

        {/* Background Color */}
        <div>
          <label className="block text-sm font-medium mb-2">Bakgrunnsfarge</label>
          <div className="flex gap-2">
            <input
              type="color"
              value={backgroundColor}
              onChange={(e) => setBackgroundColor(e.target.value)}
              className="w-16 h-10 border rounded"
            />
            <input
              type="text"
              value={backgroundColor}
              onChange={(e) => setBackgroundColor(e.target.value)}
              placeholder="#ffffff"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Padding */}
        <div>
          <label className="block text-sm font-medium mb-2">Padding</label>
          <div className="flex items-center gap-2 mb-2">
            <input
              type="checkbox"
              checked={useIndividualPadding}
              onChange={(e) => setUseIndividualPadding(e.target.checked)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="text-sm">Bruk individuell padding (top, right, bottom, left)</span>
          </div>

          {useIndividualPadding ? (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Top</label>
                <input
                  type="text"
                  value={paddingTop}
                  onChange={(e) => setPaddingTop(e.target.value)}
                  placeholder="0px"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Right</label>
                <input
                  type="text"
                  value={paddingRight}
                  onChange={(e) => setPaddingRight(e.target.value)}
                  placeholder="0px"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Bottom</label>
                <input
                  type="text"
                  value={paddingBottom}
                  onChange={(e) => setPaddingBottom(e.target.value)}
                  placeholder="0px"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Left</label>
                <input
                  type="text"
                  value={paddingLeft}
                  onChange={(e) => setPaddingLeft(e.target.value)}
                  placeholder="0px"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          ) : (
            <input
              type="text"
              value={padding}
              onChange={(e) => setPadding(e.target.value)}
              placeholder="32px eller 16px 24px 32px 16px"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          )}
          <p className="text-xs text-gray-500 mt-1">
            Eksempel: &quot;32px&quot; eller &quot;16px 24px 32px 16px&quot; (top right bottom left)
          </p>
        </div>

        {/* Border Radius */}
        <div>
          <label className="block text-sm font-medium mb-2">Avrundede hjørner</label>
          <input
            type="text"
            value={borderRadius}
            onChange={(e) => setBorderRadius(e.target.value)}
            placeholder="0px eller 8px"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <p className="text-xs text-gray-500 mt-1">
            Eksempel: &quot;0px&quot;, &quot;8px&quot;, &quot;16px&quot; eller &quot;8px 4px&quot; (top-left/bottom-right top-right/bottom-left)
          </p>
        </div>

        {/* Margin */}
        <div>
          <label className="block text-sm font-medium mb-2">Margin</label>
          <input
            type="text"
            value={margin}
            onChange={(e) => setMargin(e.target.value)}
            placeholder="0px eller 16px 24px"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <p className="text-xs text-gray-500 mt-1">
            Eksempel: &quot;0px&quot;, &quot;16px&quot; eller &quot;16px 24px&quot; (top/bottom left/right)
          </p>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
          >
            Avbryt
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Lagre
          </button>
        </div>
      </div>
    </PMDialog>
  );
};

// Text Editor Dialog Component
const TextEditorDialog = ({ open, onClose, block, onSave }) => {
  const [content, setContent] = useState(block?.content || "");
  const [styles, setStyles] = useState(block?.styles || {});
  const isRichTextBlock = block?.type === BLOCK_TYPES.TEXT;

  useEffect(() => {
    if (block) {
      setContent(block.content || "");
      setStyles(block.styles || {});
    }
  }, [block]);

  const handleSave = () => {
    onSave(styles, content);
  };

  const dialogTitle =
    block?.type === BLOCK_TYPES.HEADER ? "Rediger overskrift" : "Rediger tekst";

  return (
    <PMDialog open={open} onClose={onClose} title={dialogTitle} size={isRichTextBlock ? "4xl" : "2xl"}>
      <div className="space-y-5">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            {isRichTextBlock ? "Innhold (HTML / rik tekst)" : "Innhold"}
          </label>
          {isRichTextBlock ? (
            <div className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm">
              <QuillEditor
                value={content}
                onChange={setContent}
                placeholder="Skriv tekst, bruk verktøylinjen for lister, fet skrift, lenker …"
              />
            </div>
          ) : (
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-xl min-h-[100px] text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
              placeholder="Skriv overskrift her..."
            />
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Font størrelse</label>
            <input
              type="text"
              value={styles.fontSize || "16px"}
              onChange={(e) => setStyles({ ...styles, fontSize: e.target.value })}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
              placeholder="16px"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Font vekt</label>
            <select
              value={styles.fontWeight || "normal"}
              onChange={(e) => setStyles({ ...styles, fontWeight: e.target.value })}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-xl bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
            >
              <option value="normal">Normal</option>
              <option value="bold">Bold</option>
              <option value="lighter">Lighter</option>
              <option value="100">100</option>
              <option value="200">200</option>
              <option value="300">300</option>
              <option value="400">400</option>
              <option value="500">500</option>
              <option value="600">600</option>
              <option value="700">700</option>
              <option value="800">800</option>
              <option value="900">900</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Tekst farge</label>
            <div className="flex gap-2">
              <input
                type="color"
                value={styles.color || "#000000"}
                onChange={(e) => setStyles({ ...styles, color: e.target.value })}
                className="w-16 h-11 border border-slate-200 rounded-lg cursor-pointer"
              />
              <input
                type="text"
                value={styles.color || "#000000"}
                onChange={(e) => setStyles({ ...styles, color: e.target.value })}
                className="flex-1 px-3 py-2.5 border border-slate-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                placeholder="#000000"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Tekst justering</label>
            <select
              value={styles.textAlign || "left"}
              onChange={(e) => setStyles({ ...styles, textAlign: e.target.value })}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-xl bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
            >
              <option value="left">Venstre</option>
              <option value="center">Senter</option>
              <option value="right">Høyre</option>
              <option value="justify">Justert</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Font familie</label>
            <select
              value={styles.fontFamily || "inherit"}
              onChange={(e) => setStyles({ ...styles, fontFamily: e.target.value })}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-xl bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
            >
              <option value="inherit">Standard</option>
              <option value="Arial, sans-serif">Arial</option>
              <option value="Georgia, serif">Georgia</option>
              <option value="'Times New Roman', serif">Times New Roman</option>
              <option value="'Courier New', monospace">Courier New</option>
              <option value="Verdana, sans-serif">Verdana</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Padding</label>
            <input
              type="text"
              value={styles.padding || "16px"}
              onChange={(e) => setStyles({ ...styles, padding: e.target.value })}
              className="w-full px-3 py-2.5 border border-slate-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
              placeholder="16px"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t border-slate-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 rounded-xl bg-slate-100 text-slate-800 font-medium hover:bg-slate-200 transition-colors"
          >
            Avbryt
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="px-4 py-2.5 rounded-xl bg-blue-600 text-white font-medium hover:bg-blue-700 shadow-sm transition-colors"
          >
            Lagre
          </button>
        </div>
      </div>
    </PMDialog>
  );
};

// Image Upload Dialog Component
const ImageUploadDialog = ({
  open,
  onClose,
  filesToUpload,
  module,
  moduleParam,
  uploadFunctionRef,
  onUploadComplete,
  onProgressChange,
  onFileUploadComplete,
  isUploading,
  uploadProgress,
  uploadedCount,
  setIsUploading,
  setUploadedCount,
  setUploadProgress,
  resetUploader,
  setResetUploader,
  fileInputRef,
  handleFileSelection,
  onTitleChange,
  onDeleteFile,
}) => {
  const handleAddMoreFiles = () => {
    if (fileInputRef?.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <PMDialog open={open} onClose={onClose} title="Forhåndsvisning og opplasting" size="2xl">
      {filesToUpload.length > 0 && (
        <>
          {/* File Previews */}
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {filesToUpload.map((file, index) => (
              <div
                key={index}
                className="flex flex-col gap-3 py-4 bg-gray-100 rounded-xl px-4 relative"
              >
                <button
                  type="button"
                  onClick={() => onDeleteFile(index)}
                  className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1.5"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
                {file.previewUrl && (
                  <img
                    src={file.previewUrl}
                    className="h-40 object-cover rounded"
                    alt="Preview"
                  />
                )}
                <PMField
                  name="title"
                  type="text"
                  label="Tittel"
                  value={file.title}
                  onChange={(e) => onTitleChange(index, e.target.value)}
                />
              </div>
            ))}
          </div>

          {/* Progress Bar */}
          {isUploading && (
            <div className="mt-4 w-full bg-gray-300 rounded h-4">
              <div
                className="h-full bg-blue-500 rounded"
                style={{
                  width: `${
                    (Object.values(uploadProgress).reduce((acc, val) => acc + val, 0) /
                      (filesToUpload.length * 100)) *
                    100
                  }%`,
                }}
              />
            </div>
          )}

          {/* Upload Status */}
          {isUploading && (
            <p className="text-center mt-2 text-sm text-gray-700">
              Opplasting {uploadedCount + 1} av {filesToUpload.length}
            </p>
          )}

          {/* Upload Complete */}
          {uploadedCount === filesToUpload.length && isUploading && (
            <div className="flex justify-center items-center mt-4 text-green-500">
              <CheckCircleIcon className="h-6 w-6" />
              <span className="ml-2 text-green-600 font-semibold">Opplasting fullført</span>
            </div>
          )}

          {/* Add More Files Button */}
          {!isUploading && (
            <button
              type="button"
              className="mt-4 flex items-center justify-center w-full bg-blue-500 text-white p-2 rounded hover:bg-blue-600 transition-colors"
              onClick={handleAddMoreFiles}
            >
              <CameraIcon className="h-5 w-5 mr-2" /> Legg til flere bilder
            </button>
          )}

          {/* Actions */}
          <div className="flex justify-between mt-4">
            <button
              className="w-20 bg-gray-600 text-white p-2 rounded"
              onClick={onClose}
            >
              Steng
            </button>
            <button
              disabled={isUploading || !uploadFunctionRef.current}
              className="w-20 p-2 rounded text-white bg-emerald-600 hover:bg-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
              onClick={async () => {
                if (!isUploading && typeof uploadFunctionRef.current === "function") {
                  try {
                    await uploadFunctionRef.current();
                    // Upload complete handled by callback
                  } catch (error) {
                    console.error("Upload error:", error);
                    toast.error("Feil ved opplasting");
                  }
                }
              }}
            >
              {isUploading ? "Laster opp..." : "Last opp"}
            </button>
          </div>
        </>
      )}
    </PMDialog>
  );
};

// Page Renderer Component (for webshop)
export const PageRenderer = ({ blocks }) => {
  const renderBlock = (block) => {
    switch (block.type) {
      case BLOCK_TYPES.TEXT:
        return (
          <div key={block.id} style={block.styles}>
            <div
              className={PAGEBUILDER_RICH_HTML_CLASS}
              dangerouslySetInnerHTML={{ __html: block.content || "" }}
            />
          </div>
        );

      case BLOCK_TYPES.HEADER: {
        const HeaderTag = `h${block.level || 1}`;
        return (
          <HeaderTag key={block.id} style={block.styles}>
            {block.content}
          </HeaderTag>
        );
      }

      case BLOCK_TYPES.IMAGE:
        return block.src ? (
          <img
            key={block.id}
            src={block.src}
            alt={block.alt}
            style={block.styles}
            className="w-full h-auto rounded"
          />
        ) : null;

      case BLOCK_TYPES.BUTTON:
        return (
          <a
            key={block.id}
            href={block.link || "#"}
            style={block.styles}
            className="inline-block"
          >
            {block.text || "Klikk her"}
          </a>
        );

      case BLOCK_TYPES.SECTION:
        return (
          <div
            key={block.id}
            style={{
              backgroundColor: block.backgroundColor || "#ffffff",
              padding: block.padding || "32px",
              borderRadius: block.borderRadius || "0px",
              margin: block.margin || "0px",
            }}
            className="rounded"
          >
            {block.children?.map((child) => renderBlock(child))}
          </div>
        );

      case BLOCK_TYPES.COLUMNS:
        return (
          <div
            key={block.id}
            style={{
              display: "grid",
              gridTemplateColumns: `repeat(${block.columns}, 1fr)`,
              gap: block.gap,
            }}
            className="rounded"
          >
            {block.children?.map((column, colIdx) => (
              <div key={colIdx}>
                {column.map((child) => renderBlock(child))}
              </div>
            ))}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div>
      {blocks.map((block) => renderBlock(block))}
    </div>
  );
};
